/**
 * Archivo: recargo-cart-display.js
 * Propósito: Calcular y mostrar recargo en minicarrito
 * 
 * FASE 3A: Solo visuales (NO añade productos al carrito real)
 * IMPORTANTE: Los descuentos están reflejados automáticamente
 * 
 * Theme: Local (adaptado con selectores específicos)
 */

(function() {
  'use strict';
  
  const DEBUG_MODE = false; // Cambiar a true para debugging
  let cartObserver = null;
  let isUpdating = false;
  
  function debugLog(message, data) {
    if (DEBUG_MODE) {
      if (data !== undefined) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }
  
  // Verificar que el sistema de recargo está activo
  if (!window.ShopifyRecargo || !window.ShopifyRecargo.needsRecargo) {
    debugLog('✓ Cliente no necesita recargo, cart-display inactivo');
    return;
  }
  
  if (!window.ShopifyRecargo.isLoggedIn) {
    debugLog('⚠️ Cliente no está logueado, cart-display inactivo');
    return;
  }
  
  debugLog('🛒 Inicializando recargo-cart-display');
  
  // Endpoint de la API (App Proxy)
  const API_ENDPOINT = '/apps/recargo/calculate';
  
  // Cache simple para evitar recalcular constantemente
  let lastCartState = null;
  let lastRecargoData = null;
  let isCalculating = false;
  
  /**
   * Actualiza la visualización del recargo en el carrito
   */
  async function updateRecargoDisplay() {
    // Evitar llamadas concurrentes
    if (isCalculating) {
      debugLog('⏳ Ya hay un cálculo en proceso, omitiendo...');
      return;
    }
    
    try {
      isCalculating = true;
      debugLog('📊 Actualizando display de recargo...');
      
      // Obtener estado actual del carrito
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      debugLog('🛒 Carrito obtenido:', {
        items: cart.items.length,
        total: cart.total_price,
        discount: cart.total_discount
      });
      
      // Verificar si el carrito cambió (evitar llamadas innecesarias)
      // Incluir total_discount en el estado para detectar cambios en descuentos
      const cartStateKey = JSON.stringify({
        items: cart.items.map(i => ({
          id: i.variant_id,
          qty: i.quantity,
          price: i.price,
          line_price: i.line_price
        })),
        total_discount: cart.total_discount
      });
      
      if (cartStateKey === lastCartState && lastRecargoData) {
        debugLog('✓ Carrito sin cambios, usando cache');
        displayRecargoInCart(lastRecargoData);
        return;
      }
      
      lastCartState = cartStateKey;
      
      // Si el carrito está vacío, limpiar recargo
      if (cart.items.length === 0) {
        debugLog('🗑️ Carrito vacío, limpiando recargo');
        removeRecargoDisplay();
        lastRecargoData = null;
        return;
      }
      
      // Preparar datos para enviar a la API
      const cartData = {
        items: cart.items.map(item => ({
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price, // Precio unitario original en cents
          line_price: item.line_price, // Precio línea con descuentos
          original_line_price: item.original_line_price,
          total_discount: item.total_discount,
          title: item.title,
          product_title: item.product_title,
          variant_title: item.variant_title
        })),
        currency: cart.currency,
        total_discount: cart.total_discount
      };
      
      // Llamar a la API para calcular recargo
      debugLog('📡 Llamando a API de cálculo...');
      
      const recargoResponse = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });
      
      if (!recargoResponse.ok) {
        throw new Error(`HTTP ${recargoResponse.status}: ${recargoResponse.statusText}`);
      }
      
      const result = await recargoResponse.json();
      
      debugLog('✅ Respuesta de API:', result);
      
      if (!result.success || !result.needsRecargo || !result.recargo) {
        debugLog('⚠️ No se calculó recargo según API');
        removeRecargoDisplay();
        return;
      }
      
      // Guardar en cache
      lastRecargoData = result.recargo;
      
      // Mostrar en el DOM
      displayRecargoInCart(result.recargo);
      
    } catch (error) {
      console.error('❌ Error calculando recargo:', error);
      // Fail silently en producción para no romper la experiencia
      if (DEBUG_MODE) {
        // Mostrar error solo en desarrollo
        const errorMsg = `Error: ${error.message}`;
        console.error(errorMsg);
      }
    } finally {
      isCalculating = false;
    }
  }
  
  /**
   * Renderiza el recargo en el carrito
   * @param {Object} recargoData - Datos del recargo calculado
   */
  function displayRecargoInCart(recargoData) {
    // Desconectar observer temporalmente mientras renderizamos
    if (cartObserver) {
      cartObserver.disconnect();
      debugLog('⏸️ Observer desconectado temporalmente');
    }
    
    debugLog('🎨 Renderizando recargo en cart:', recargoData);
    
    // Selectores adaptados al theme "Local"
    // Minicarrito: sidebar-drawer#site-cart-sidebar > .sidebar__footer > #AjaxCartSubtotal > .cart__details
    // Carrito: #AjaxCartSubtotal > .cart__details
    const cartTotalsSelectors = [
      '#AjaxCartSubtotal .cart__details',  // Theme Local (minicarrito + carrito)
      '.sidebar__footer .cart__details',   // Theme Local (minicarrito específico)
      '.cart__details',                    // Theme Local (fallback)
      '.cart-drawer__footer',              // Dawn theme (fallback)
      '.cart__footer'                      // Debut theme (fallback)
    ];
    
    let cartTotals = null;
    for (const selector of cartTotalsSelectors) {
      cartTotals = document.querySelector(selector);
      if (cartTotals) {
        debugLog(`✓ Cart totals encontrado: ${selector}`);
        break;
      }
    }
    
    if (!cartTotals) {
      console.warn('⚠️ No se encontró contenedor de totales del carrito');
      console.warn('Selectores probados:', cartTotalsSelectors);
      console.warn('💡 Por favor, inspecciona el HTML del minicarrito y actualiza los selectores');
      return;
    }
    
    // Eliminar recargo anterior si existe
    removeRecargoDisplay();
    
    // Crear contenedor de recargo
    const recargoContainer = document.createElement('div');
    recargoContainer.id = 'recargo-equivalencia-container';
    recargoContainer.className = 'recargo-equivalencia-container';
    
    // Crear líneas de recargo (una por tramo)
    let recargoHTML = '';
    
    recargoData.tramos.forEach(tramo => {
      // Formatear porcentajes para evitar errores de precisión flotante
      const recargoRate = formatPercentage(tramo.recargo_rate);
      const taxRate = formatPercentage(tramo.tax_rate);
      
      recargoHTML += `
        <div class="recargo-line" data-iva="${tramo.iva_code}">
          <span class="recargo-label">
            <span class="recargo-text">Recargo Equiv. ${recargoRate}% (IVA ${taxRate}%)</span>
          </span>
          <span class="recargo-amount">€${formatMoney(tramo.recargo_total)}</span>
        </div>
      `;
    });
    
    // Nota informativa
    const note = recargoData.is_estimated 
      ? '<p class="recargo-note">⚠️ Recargo estimado. El importe final se calculará en el checkout.</p>'
      : '';
    
    recargoContainer.innerHTML = `
      <div class="recargo-lines">
        ${recargoHTML}
      </div>
      ${note}
    `;
    
    // Insertar antes del .cart__subtotal (theme Local)
    const subtotalElement = cartTotals.querySelector('.cart__subtotal');
    
    if (subtotalElement) {
      cartTotals.insertBefore(recargoContainer, subtotalElement);
      debugLog('✅ Recargo insertado antes del .cart__subtotal');
    } else {
      // Fallback: insertar al inicio
      cartTotals.insertBefore(recargoContainer, cartTotals.firstChild);
      debugLog('⚠️ No se encontró .cart__subtotal, insertado al inicio');
    }
    
    debugLog('✅ Recargo renderizado correctamente');
    
    // Actualizar el total del carrito
    updateCartTotal(recargoData.total_recargo, recargoData.currency);
    
    // Reconectar observer después de un delay
    setTimeout(() => {
      const cartContainer = document.querySelector('sidebar-drawer#site-cart-sidebar, [data-cart], #AjaxCartSubtotal');
      if (cartContainer && cartObserver) {
        cartObserver.observe(cartContainer, {
          childList: true,
          subtree: true
        });
        debugLog('▶️ Observer reconectado');
      }
    }, 200);
  }
  
  /**
   * Elimina la visualización del recargo
   */
  function removeRecargoDisplay() {
    const existing = document.getElementById('recargo-equivalencia-container');
    if (existing) {
      existing.remove();
      debugLog('🗑️ Recargo removido del DOM');
    }
    
    // Restaurar el total original
    restoreOriginalTotal();
  }
  
  /**
   * Actualiza el total del carrito incluyendo el recargo
   * @param {number} recargoTotal - Total del recargo en euros
   * @param {string} currency - Moneda (EUR, USD, etc.)
   */
  function updateCartTotal(recargoTotal, currency) {
    // Selectores para el elemento del total
    const totalSelectors = [
      '.cart__total strong.text-size--heading',  // Theme Local (principal)
      '.cart__total strong',                      // Theme Local (fallback)
      '.cart__subtotal .cart__subtotal-price',
      '.cart__price--subtotal',
      '.totals__subtotal-value',
      '[data-cart-subtotal]'
    ];
    
    let totalElement = null;
    for (const selector of totalSelectors) {
      totalElement = document.querySelector(selector);
      if (totalElement) {
        debugLog(`✓ Total element encontrado: ${selector}`);
        break;
      }
    }
    
    if (!totalElement) {
      debugLog('⚠️ No se encontró elemento del total para actualizar');
      return;
    }
    
    // Guardar el total original si aún no lo hemos hecho
    if (!totalElement.dataset.originalTotal) {
      totalElement.dataset.originalTotal = totalElement.textContent.trim();
      debugLog(`💾 Total original guardado: ${totalElement.dataset.originalTotal}`);
    }
    
    // Extraer el total actual (puede tener formato €21,90 o 21,90 EUR)
    const originalText = totalElement.dataset.originalTotal;
    const originalAmount = parseFloat(originalText.replace(/[^0-9,]/g, '').replace(',', '.'));
    
    if (isNaN(originalAmount)) {
      debugLog('⚠️ No se pudo parsear el total original');
      return;
    }
    
    // Calcular nuevo total
    const newTotal = originalAmount + recargoTotal;
    
    // Formatear siempre como €XX,XX para consistencia con el theme
    const formattedTotal = `€${formatMoney(newTotal)}`;
    
    totalElement.textContent = formattedTotal;
    debugLog(`💰 Total actualizado: ${originalAmount}€ + ${recargoTotal}€ = ${newTotal}€`);
  }
  
  /**
   * Restaura el total original del carrito
   */
  function restoreOriginalTotal() {
    const totalSelectors = [
      '.cart__total strong.text-size--heading',  // Theme Local (principal)
      '.cart__total strong',                      // Theme Local (fallback)
      '.cart__subtotal .cart__subtotal-price',
      '.cart__price--subtotal',
      '.totals__subtotal-value',
      '[data-cart-subtotal]'
    ];
    
    let totalElement = null;
    for (const selector of totalSelectors) {
      totalElement = document.querySelector(selector);
      if (totalElement && totalElement.dataset.originalTotal) {
        totalElement.textContent = totalElement.dataset.originalTotal;
        delete totalElement.dataset.originalTotal;
        debugLog('🔄 Total original restaurado');
        break;
      }
    }
  }
  
  /**
   * Formatea porcentaje eliminando errores de precisión
   * @param {number} percentage - Porcentaje (1.4, 10, 21)
   * @returns {string} - Porcentaje formateado sin decimales innecesarios
   */
  function formatPercentage(percentage) {
    // Redondear para eliminar errores de punto flotante
    const rounded = Math.round(percentage * 100) / 100;
    
    // Si es entero, mostrar sin decimales
    if (rounded % 1 === 0) {
      return rounded.toString();
    }
    
    // Si tiene decimales, mostrar con 1-2 decimales según necesidad
    return rounded.toString().replace(/\.?0+$/, '');
  }
  
  /**
   * Formatea cantidad monetaria
   * @param {number} amount - Cantidad en formato decimal (12.45)
   * @returns {string} - Cantidad formateada (12,45)
   */
  function formatMoney(amount) {
    // Redondear a 2 decimales para evitar decimales excesivos
    const rounded = (Math.round(amount * 100) / 100).toFixed(2);
    return rounded.replace('.', ',');
  }
  
  // ============================================
  // INICIALIZACIÓN Y EVENT LISTENERS
  // ============================================
  
  // Actualizar cuando cambie el carrito (eventos nativos de Shopify themes)
  document.addEventListener('cart:updated', () => {
    debugLog('🔄 Evento cart:updated detectado');
    updateRecargoDisplay();
  });
  
  document.addEventListener('cart:changed', () => {
    debugLog('🔄 Evento cart:changed detectado');
    updateRecargoDisplay();
  });
  
  // También para themes con Ajax Cart API custom
  document.addEventListener('ajaxCart:updated', () => {
    debugLog('🔄 Evento ajaxCart:updated detectado');
    updateRecargoDisplay();
  });
  
  // Observer para detectar cambios en el DOM del carrito (themes dinámicos)
  if (typeof window.MutationObserver !== 'undefined') {
    cartObserver = new MutationObserver((mutations) => {
      // Ignorar si estamos actualizando
      if (isUpdating) {
        debugLog('🔄 Actualización en curso, ignorando mutation');
        return;
      }
      
      // Filtrar mutations: ignorar cambios en nuestro contenedor de recargo
      const relevantMutations = mutations.filter(mutation => {
        // Ignorar si el cambio es dentro de nuestro contenedor de recargo
        if (mutation.target.id === 'recargo-equivalencia-container') {
          return false;
        }
        if (mutation.target.closest && mutation.target.closest('#recargo-equivalencia-container')) {
          return false;
        }
        
        // Ignorar si algún nodo añadido es nuestro contenedor de recargo
        if (mutation.addedNodes.length > 0) {
          for (let node of mutation.addedNodes) {
            if (node.id === 'recargo-equivalencia-container') {
              return false;
            }
            if (node.nodeType === Node.ELEMENT_NODE && node.querySelector && node.querySelector('#recargo-equivalencia-container')) {
              return false;
            }
          }
        }
        
        return true;
      });
      
      // Si no hay mutations relevantes, no hacer nada
      if (relevantMutations.length === 0) {
        debugLog('👁️ MutationObserver: cambios ignorados (solo recargo)');
        return;
      }
      
      debugLog('👁️ MutationObserver: cambio real en carrito detectado');
      
      // Marcar que estamos actualizando
      isUpdating = true;
      
      // Procesar los cambios
      updateRecargoDisplay();
      
      // Restaurar el flag después de un breve delay
      setTimeout(() => {
        isUpdating = false;
      }, 100);
    });
    
    // Observar el sidebar del minicarrito (theme Local)
    const cartContainer = document.querySelector('sidebar-drawer#site-cart-sidebar, [data-cart], #AjaxCartSubtotal');
    if (cartContainer) {
      cartObserver.observe(cartContainer, {
        childList: true,
        subtree: true
      });
      debugLog('✓ MutationObserver activado en:', cartContainer.id || cartContainer.className);
    } else {
      debugLog('⚠️ No se encontró contenedor del carrito para observar');
    }
  }
  
  // Actualizar al cargar la página
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateRecargoDisplay);
  } else {
    // Si el DOM ya está listo, actualizar inmediatamente
    setTimeout(updateRecargoDisplay, 500); // Pequeño delay para asegurar que todo esté cargado
  }
  
  debugLog('✅ recargo-cart-display inicializado completamente');
})();
